/**
 * ArtifactDetailPage - Public Artifact Detail View
 * Elvis Aloha Collection
 * 
 * Museum-quality individual artifact page with:
 * - Hero image with lightbox gallery
 * - Full artifact documentation
 * - Historical background & provenance
 * - Valuation with USD/JPY conversion
 * - Related artifacts & research
 * - Contact inquiry form
 * - SEO metadata & structured data
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowLeft, ChevronLeft, ChevronRight, ZoomIn, X, 
  Mail, MapPin, Calendar, Tag, Package, Award, 
  Shield, FileText, ExternalLink, Share2, Copy, Check
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// ============================================================================
// LIGHTBOX COMPONENT
// ============================================================================

const ImageLightbox = ({ images, currentIndex, isOpen, onClose, onNext, onPrev }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const handleKey = (e) => {
        if (e.key === 'Escape') onClose();
        if (e.key === 'ArrowRight') onNext();
        if (e.key === 'ArrowLeft') onPrev();
      };
      window.addEventListener('keydown', handleKey);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleKey);
      };
    }
  }, [isOpen, onClose, onNext, onPrev]);
  
  if (!isOpen || !images || images.length === 0) return null;
  
  const current = images[currentIndex];
  const imageSrc = current?.file_path ? `${API_BASE}${current.file_path}` : '';
  
  return (
    <div 
      className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center"
      onClick={onClose}
      data-testid="artifact-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-3 text-white/80 hover:text-white z-10"
        aria-label="Close lightbox"
      >
        <X size={28} />
      </button>
      
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 text-white/80 hover:text-white z-10 bg-black/30 rounded-full"
            aria-label="Previous image"
          >
            <ChevronLeft size={32} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 text-white/80 hover:text-white z-10 bg-black/30 rounded-full"
            aria-label="Next image"
          >
            <ChevronRight size={32} />
          </button>
        </>
      )}
      
      <div 
        className="max-w-[90vw] max-h-[90vh] relative"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={imageSrc}
          alt={current?.alt_text || current?.caption || ''}
          className="max-w-full max-h-[90vh] object-contain"
        />
        {current?.caption && (
          <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white/90 px-4 py-3 text-sm">
            {current.caption}
          </div>
        )}
      </div>
      
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm">
          {currentIndex + 1} / {images.length}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// INQUIRY FORM COMPONENT
// ============================================================================

const InquiryForm = ({ artifact }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    country: '',
    inquiry_type: 'general',
    message: '',
    consent_given: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consent_given) {
      setError('Please confirm your consent to proceed.');
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      const response = await fetch(`${API_BASE}/api/collection-items/public/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          artifact_id: artifact.id
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to submit inquiry');
      }
      
      setSubmitted(true);
      toast.success('Your inquiry has been submitted. We will respond shortly.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  if (submitted) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check size={32} className="text-green-600" />
        </div>
        <h3 className="text-xl font-semibold mb-2">Inquiry Submitted</h3>
        <p className="text-gray-600">
          Thank you for your interest. We will respond to your email shortly.
        </p>
      </div>
    );
  }
  
  const inputClass = "w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500 bg-white";
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
            className={inputClass}
            placeholder="Your full name"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
          <input
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
            className={inputClass}
            placeholder="your@email.com"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
          <input
            type="text"
            value={formData.country}
            onChange={(e) => setFormData(p => ({ ...p, country: e.target.value }))}
            className={inputClass}
            placeholder="Your country"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Inquiry Type</label>
          <select
            value={formData.inquiry_type}
            onChange={(e) => setFormData(p => ({ ...p, inquiry_type: e.target.value }))}
            className={inputClass}
          >
            <option value="general">General Inquiry</option>
            <option value="purchase">Purchase Inquiry</option>
            <option value="additional_photos">Request Additional Photos</option>
            <option value="condition_info">Condition Information</option>
            <option value="provenance_question">Provenance Question</option>
            <option value="research_question">Research Question</option>
          </select>
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
        <textarea
          required
          rows={4}
          value={formData.message}
          onChange={(e) => setFormData(p => ({ ...p, message: e.target.value }))}
          className={inputClass}
          placeholder="Your inquiry or message..."
        />
      </div>
      
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          id="consent"
          checked={formData.consent_given}
          onChange={(e) => setFormData(p => ({ ...p, consent_given: e.target.checked }))}
          className="mt-1"
        />
        <label htmlFor="consent" className="text-sm text-gray-600">
          I consent to having my inquiry and contact information stored for the purpose of responding to this request. 
          This information will not be shared with third parties.
        </label>
      </div>
      
      <button
        type="submit"
        disabled={submitting}
        className="w-full py-3 px-6 bg-amber-700 hover:bg-amber-800 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? 'Submitting...' : 'Send Inquiry'}
      </button>
    </form>
  );
};

// ============================================================================
// STATUS DISPLAY HELPERS
// ============================================================================

const STATUS_COLORS = {
  available_private_sale: { bg: 'bg-green-100', text: 'text-green-800', label: 'Available for Private Sale' },
  price_on_request: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Price on Request' },
  open_to_offers: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Inquiries Invited' },
  not_currently_available: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Not Currently Available' },
  permanent_collection: { bg: 'bg-sky-100', text: 'text-sky-800', label: 'Permanent Collection' },
  consignment: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Available' },
  scheduled_for_auction: { bg: 'bg-pink-100', text: 'text-pink-800', label: 'Upcoming Auction' },
  at_auction: { bg: 'bg-red-100', text: 'text-red-800', label: 'At Auction' },
  reserved: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Reserved' },
  sale_pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Sale Pending' },
  sold: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Sold' },
  withdrawn: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Not Available' },
  archived: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Not Available' }
};

const AvailabilityBadge = ({ status }) => {
  const config = STATUS_COLORS[status] || STATUS_COLORS.not_currently_available;
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

const CATEGORY_LABELS = {
  ticket: 'Concert Ticket',
  design_mockup: 'Design Mock-up',
  production_material: 'Production Material',
  document: 'Historical Document',
  photograph: 'Photograph',
  memorabilia: 'Memorabilia',
  costume: 'Costume',
  award: 'Award',
  certification: 'Certification',
  other: 'Artifact'
};

const RELATIONSHIP_LABELS = {
  aloha_direct: 'Aloha from Hawaii 1973',
  aloha_related: 'Aloha Related',
  elvis_general: 'Elvis Presley',
  historical_context: 'Historical Context',
  other: 'Collection Item'
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ArtifactDetailPage = () => {
  const { itemSlug } = useParams();
  const navigate = useNavigate();
  
  const [artifact, setArtifact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  
  useEffect(() => {
    loadArtifact();
  }, [itemSlug]);
  
  const loadArtifact = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE}/api/collection-items/public/item/${itemSlug}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Artifact not found');
        } else {
          throw new Error('Failed to load artifact');
        }
        return;
      }
      
      const data = await response.json();
      setArtifact(data);
      setActiveImageIndex(0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };
  
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: artifact.title,
          text: artifact.short_summary || artifact.title,
          url: window.location.href
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          handleCopyLink();
        }
      }
    } else {
      handleCopyLink();
    }
  };
  
  // Get all public images
  const images = artifact?.images?.filter(img => img.is_public !== false) || [];
  const primaryImage = artifact?.primary_image || images[0];
  
  const nextImage = () => setActiveImageIndex(i => (i + 1) % images.length);
  const prevImage = () => setActiveImageIndex(i => (i - 1 + images.length) % images.length);
  
  // Format currency
  const formatUSD = (value) => {
    if (!value) return null;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(value);
  };
  
  const formatJPY = (value) => {
    if (!value) return null;
    return '¥' + Math.round(value).toLocaleString();
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-amber-700 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-stone-500">Loading artifact...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error || !artifact) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <h1 className="text-2xl font-semibold text-stone-800 mb-3">
            {error === 'Artifact not found' ? 'Artifact Not Found' : 'Error Loading Artifact'}
          </h1>
          <p className="text-stone-600 mb-6">
            {error === 'Artifact not found' 
              ? 'The artifact you are looking for may have been moved or is no longer available.'
              : 'There was a problem loading this artifact. Please try again.'}
          </p>
          <Link 
            to="/collection"
            className="inline-flex items-center gap-2 text-amber-700 hover:text-amber-800 font-medium"
          >
            <ArrowLeft size={18} />
            Back to Collection
          </Link>
        </div>
      </div>
    );
  }
  
  // Build structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": artifact.availability_status === 'sold' ? "Product" : "CollectionPage",
    "name": artifact.title,
    "description": artifact.short_summary || artifact.meta_description,
    "image": primaryImage?.file_path ? `${API_BASE}${primaryImage.file_path}` : undefined,
    "url": window.location.href,
    "dateCreated": artifact.date_or_period,
    "category": CATEGORY_LABELS[artifact.category] || artifact.category,
    ...(artifact.show_appraised_value && artifact.appraised_value_usd && {
      "offers": {
        "@type": "Offer",
        "price": artifact.appraised_value_usd,
        "priceCurrency": "USD",
        "availability": artifact.availability_status === 'available_private_sale' 
          ? "https://schema.org/InStock" 
          : "https://schema.org/OutOfStock"
      }
    })
  };
  
  const pageTitle = artifact.seo_title || artifact.title;
  const pageDescription = artifact.meta_description || artifact.short_summary || `${artifact.title} - Elvis Aloha Collection`;
  const pageImage = primaryImage?.file_path ? `${API_BASE}${primaryImage.file_path}` : '';
  
  return (
    <>
      {/* SEO Metadata */}
      <Helmet>
        <title>{`${pageTitle} | Elvis Aloha Collection`}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={window.location.href} />
        
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        {pageImage && <meta property="og:image" content={pageImage} />}
        <meta property="og:url" content={window.location.href} />
        
        {/* Twitter/X Card */}
        <meta name="twitter:card" content={pageImage ? "summary_large_image" : "summary"} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {pageImage && <meta name="twitter:image" content={pageImage} />}
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      
      {/* Lightbox */}
      <ImageLightbox
        images={images}
        currentIndex={activeImageIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onNext={nextImage}
        onPrev={prevImage}
      />
      
      <div className="min-h-screen bg-stone-50" data-testid="artifact-detail-page">
        {/* Header Navigation */}
        <div className="bg-white border-b border-stone-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <Link 
                to="/collection"
                className="inline-flex items-center gap-2 text-stone-600 hover:text-amber-700 transition-colors"
                data-testid="artifact-back-link"
              >
                <ArrowLeft size={18} />
                <span className="text-sm font-medium">Back to Collection</span>
              </Link>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={handleShare}
                  className="p-2 text-stone-500 hover:text-amber-700 transition-colors"
                  aria-label="Share artifact"
                >
                  <Share2 size={20} />
                </button>
                <button
                  onClick={handleCopyLink}
                  className="p-2 text-stone-500 hover:text-amber-700 transition-colors"
                  aria-label="Copy link"
                >
                  {copied ? <Check size={20} className="text-green-600" /> : <Copy size={20} />}
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            
            {/* Left Column - Images */}
            <div className="lg:col-span-7">
              <div className="lg:sticky lg:top-24 space-y-4">
                {/* Main Image */}
                <div className="relative group">
                  <button
                    onClick={() => setLightboxOpen(true)}
                    className="block w-full bg-white border border-stone-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                    aria-label="View full size image"
                    data-testid="artifact-main-image"
                  >
                    {images.length > 0 ? (
                      <img
                        src={`${API_BASE}${images[activeImageIndex]?.file_path || primaryImage?.file_path}`}
                        alt={images[activeImageIndex]?.alt_text || artifact.title}
                        className="w-full aspect-[4/3] object-contain p-4"
                      />
                    ) : (
                      <div className="w-full aspect-[4/3] bg-stone-100 flex items-center justify-center">
                        <Package size={64} className="text-stone-300" />
                      </div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/10">
                      <div className="bg-white/90 rounded-full p-3">
                        <ZoomIn size={24} className="text-stone-700" />
                      </div>
                    </div>
                  </button>
                  
                  {/* Image navigation arrows */}
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-md transition-colors"
                        aria-label="Previous image"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-md transition-colors"
                        aria-label="Next image"
                      >
                        <ChevronRight size={20} />
                      </button>
                    </>
                  )}
                </div>
                
                {/* Thumbnail Gallery */}
                {images.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {images.map((img, index) => (
                      <button
                        key={img.id || index}
                        onClick={() => setActiveImageIndex(index)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                          index === activeImageIndex 
                            ? 'border-amber-700' 
                            : 'border-stone-200 hover:border-amber-400'
                        }`}
                      >
                        <img
                          src={`${API_BASE}${img.file_path}`}
                          alt={img.alt_text || `Image ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Right Column - Details */}
            <div className="lg:col-span-5 space-y-8">
              {/* Header */}
              <div>
                {/* Category & Relationship badges */}
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-stone-100 rounded text-xs font-medium text-stone-600">
                    <Tag size={12} />
                    {CATEGORY_LABELS[artifact.category] || artifact.category}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 rounded text-xs font-medium text-amber-800">
                    {RELATIONSHIP_LABELS[artifact.relationship] || artifact.relationship}
                  </span>
                </div>
                
                {/* Title */}
                <h1 className="text-2xl sm:text-3xl font-serif font-semibold text-stone-900 mb-2" data-testid="artifact-title">
                  {artifact.title}
                </h1>
                
                {/* Inventory Number */}
                <p className="text-sm text-stone-500 font-mono">
                  {artifact.inventory_number}
                </p>
              </div>
              
              {/* Availability & Price */}
              <div className="bg-white border border-stone-200 rounded-xl p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-stone-600">Availability</span>
                  <AvailabilityBadge status={artifact.availability_status} />
                </div>
                
                {artifact.show_appraised_value && artifact.appraised_value_usd && (
                  <div className="pt-4 border-t border-stone-100">
                    <div className="text-sm text-stone-500 mb-1">Appraised Value</div>
                    <div className="text-2xl font-semibold text-stone-900">
                      {formatUSD(artifact.appraised_value_usd)}
                    </div>
                    {/* JPY conversion - only show if available */}
                    {artifact.appraised_value_jpy ? (
                      <div className="mt-2">
                        <div className="text-sm text-stone-500">
                          Estimated Japanese Yen Value
                        </div>
                        <div className="text-lg font-medium text-stone-700">
                          Approximately {formatJPY(artifact.appraised_value_jpy)}
                        </div>
                        {artifact.exchange_rate_info && (
                          <div className="text-xs text-stone-400 mt-1 space-y-0.5">
                            {artifact.exchange_rate_info.provider_rate_date && (
                              <div>Rate date: {new Date(artifact.exchange_rate_info.provider_rate_date + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                            )}
                            <div>{artifact.exchange_rate_info.disclaimer || 'Currency conversion is provided for reference only.'}</div>
                            {artifact.exchange_rate_info.is_stale && (
                              <div className="text-amber-600">(Using older rate - update pending)</div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : artifact.exchange_rate_info?.status === 'unavailable' ? (
                      <div className="text-xs text-stone-400 mt-2">
                        JPY conversion temporarily unavailable
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
              
              {/* Summary */}
              {artifact.short_summary && (
                <div>
                  <p className="text-stone-700 leading-relaxed">
                    {artifact.short_summary}
                  </p>
                </div>
              )}
              
              {/* Quick Details */}
              <div className="grid grid-cols-2 gap-4">
                {artifact.date_or_period && (
                  <div className="flex items-start gap-3">
                    <Calendar size={18} className="text-stone-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-stone-500 uppercase tracking-wide">Date/Period</div>
                      <div className="text-sm text-stone-800">{artifact.date_or_period}</div>
                    </div>
                  </div>
                )}
                {artifact.dimensions && (
                  <div className="flex items-start gap-3">
                    <Package size={18} className="text-stone-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-stone-500 uppercase tracking-wide">Dimensions</div>
                      <div className="text-sm text-stone-800">{artifact.dimensions}</div>
                    </div>
                  </div>
                )}
                {artifact.materials && (
                  <div className="flex items-start gap-3">
                    <FileText size={18} className="text-stone-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-stone-500 uppercase tracking-wide">Materials</div>
                      <div className="text-sm text-stone-800">{artifact.materials}</div>
                    </div>
                  </div>
                )}
                {artifact.certification && (
                  <div className="flex items-start gap-3">
                    <Award size={18} className="text-stone-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-stone-500 uppercase tracking-wide">Certification</div>
                      <div className="text-sm text-stone-800">{artifact.certification}</div>
                    </div>
                  </div>
                )}
                {artifact.condition_summary && (
                  <div className="flex items-start gap-3 col-span-2">
                    <Shield size={18} className="text-stone-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-xs text-stone-500 uppercase tracking-wide">Condition</div>
                      <div className="text-sm text-stone-800">{artifact.condition_summary}</div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Full Description */}
              {artifact.full_description && (
                <div>
                  <h2 className="text-lg font-semibold text-stone-900 mb-3">Description</h2>
                  <div className="prose prose-stone prose-sm max-w-none">
                    <p className="whitespace-pre-line">{artifact.full_description}</p>
                  </div>
                </div>
              )}
              
              {/* Historical Background */}
              {artifact.historical_background && (
                <div>
                  <h2 className="text-lg font-semibold text-stone-900 mb-3">Historical Background</h2>
                  <div className="prose prose-stone prose-sm max-w-none">
                    <p className="whitespace-pre-line">{artifact.historical_background}</p>
                  </div>
                </div>
              )}
              
              {/* Provenance */}
              {artifact.public_provenance && (
                <div>
                  <h2 className="text-lg font-semibold text-stone-900 mb-3">Provenance</h2>
                  <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                    <p className="text-stone-700 text-sm whitespace-pre-line">
                      {artifact.public_provenance}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Keywords */}
              {artifact.keywords && artifact.keywords.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {artifact.keywords.map((keyword, i) => (
                    <span 
                      key={i}
                      className="px-3 py-1 bg-stone-100 rounded-full text-xs text-stone-600"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Inquiry Section */}
          <div className="mt-16 pt-12 border-t border-stone-200">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-serif font-semibold text-stone-900 mb-2">
                  Inquire About This Artifact
                </h2>
                <p className="text-stone-600">
                  Interested in this item? Submit an inquiry and we will respond to your email.
                </p>
              </div>
              
              <div className="bg-white border border-stone-200 rounded-xl p-6 md:p-8">
                <InquiryForm artifact={artifact} />
              </div>
            </div>
          </div>
          
          {/* Related Artifacts */}
          {artifact.related_artifact_ids && artifact.related_artifact_ids.length > 0 && (
            <div className="mt-16 pt-12 border-t border-stone-200">
              <h2 className="text-2xl font-serif font-semibold text-stone-900 mb-6">
                Related Artifacts
              </h2>
              <p className="text-stone-500">Related artifacts will be displayed here.</p>
            </div>
          )}
        </div>
        
        {/* Mobile Bottom Spacer */}
        <div className="h-20 lg:h-0" />
      </div>
    </>
  );
};

export default ArtifactDetailPage;
